/**
 * @graphql cc
 */
import gql from 'graphql-tag';
import { useMemo } from 'react';
import { useQuery } from 'react-apollo';

import { usePageContentId } from '@confluence/page-context';
import { useContentStatus } from '@confluence/page-utils/entry-points/useContentStatus';

import { normalizeContentAttachmentsData } from '../normalizers/normalizeContentAttachmentsData';

import type {
	useContentAttachmentsDetailsPanelContentAttachmentsQuery as useContentAttachmentsDetailsPanelContentAttachmentsQuery$data,
	useContentAttachmentsDetailsPanelContentAttachmentsQueryVariables as useContentAttachmentsDetailsPanelContentAttachmentsQuery$variables,
} from './__types__/useContentAttachmentsDetailsPanelContentAttachmentsQuery';

const useContentAttachmentsDetailsPanelContentAttachmentsQuery = gql`
	query useContentAttachmentsDetailsPanelContentAttachmentsQuery(
		$contentId: ID!
		$status: String!
		$limit: Int!
	) {
		singleContent(id: $contentId, status: [$status]) {
			id
			attachments(first: $limit) {
				count
				pageInfo {
					hasNextPage
				}
			}
		}
	}
`;
export const useContentAttachments = (attachmentsLimit: number) => {
	const [contentId] = usePageContentId();

	const {
		contentStatus,
		loading: isContentStatusLoading,
		error: contentStatusError,
		refetch: refetchContentStatus,
	} = useContentStatus();

	const { data, loading, error, refetch } = useQuery<
		useContentAttachmentsDetailsPanelContentAttachmentsQuery$data,
		useContentAttachmentsDetailsPanelContentAttachmentsQuery$variables
	>(useContentAttachmentsDetailsPanelContentAttachmentsQuery, {
		variables: {
			contentId: contentId ?? '',
			status: contentStatus ?? '',
			limit: attachmentsLimit,
		},
		skip: isContentStatusLoading || !!contentStatusError || !contentStatus || !contentId,
	});
	const normalizedData = useMemo(() => normalizeContentAttachmentsData(data), [data]);

	return {
		data: normalizedData,
		loading,
		error: contentStatusError || error,
		refetch: !!contentStatusError ? refetchContentStatus : refetch,
	};
};
